import React from 'react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Default emoji presets
export const VOTE_PRESETS = {
  paws: { up: '🐾', down: '❌', name: 'Paws' },
  faces: { up: '😊', down: '🥶', name: 'Warm/Cold' },
  hearts: { up: '❤️', down: '💔', name: 'Hearts' },
  thumbs: { up: '👍', down: '👎', name: 'Thumbs' },
};

// Temperature colors for ELO scores
export const getEloTemperature = (score) => {
  if (score >= 1800) return { label: 'Hot', color: 'text-orange-600', bg: 'bg-orange-100', emoji: '🔥' };
  if (score >= 1500) return { label: 'Warm', color: 'text-yellow-600', bg: 'bg-yellow-100', emoji: '☀️' };
  if (score >= 1200) return { label: 'Neutral', color: 'text-gray-600', bg: 'bg-gray-100', emoji: '😊' };
  if (score >= 900) return { label: 'Cool', color: 'text-blue-400', bg: 'bg-blue-50', emoji: '❄️' };
  return { label: 'Cold', color: 'text-blue-600', bg: 'bg-blue-100', emoji: '🥶' };
};

/**
 * ELO Vote Buttons Component
 * 
 * @param {Object} props
 * @param {number} props.upvotes - Number of upvotes
 * @param {number} props.downvotes - Number of downvotes
 * @param {number} props.totalVotes - Total number of votes
 * @param {number} props.eloScore - Current ELO score (optional)
 * @param {number} props.userVote - Current user's vote: 1 (up), -1 (down), 0 (none)
 * @param {function} props.onVote - Callback when user votes: (voteValue) => void
 * @param {string} props.emojiUp - Upvote emoji (default: 🐾)
 * @param {string} props.emojiDown - Downvote emoji (default: ❌)
 * @param {boolean} props.showTemperature - Show temperature indicator (default: true)
 * @param {boolean} props.showVoteCount - Show vote count (default: true)
 * @param {boolean} props.disabled - Disable voting
 * @param {string} props.size - Button size: 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const EloVoteButtons = ({
  upvotes = 0,
  downvotes = 0,
  totalVotes,
  eloScore,
  userVote = 0,
  onVote,
  emojiUp = '🐾',
  emojiDown = '❌',
  showTemperature = true,
  showVoteCount = true,
  disabled = false,
  size = 'sm',
  className = '',
  testIdPrefix = 'elo',
}) => {
  const netVotes = upvotes - downvotes;
  const total = totalVotes ?? (upvotes + downvotes);
  const score = eloScore ?? (1200 + netVotes * 10); // Estimate if not provided
  const temp = getEloTemperature(score);

  const handleUpvote = () => {
    if (onVote) {
      onVote(userVote === 1 ? 0 : 1);
    }
  };

  const handleDownvote = () => {
    if (onVote) {
      onVote(userVote === -1 ? 0 : -1);
    }
  };

  const buttonSize = {
    sm: 'h-8 w-8 text-base',
    md: 'h-10 w-10 text-lg',
    lg: 'h-12 w-12 text-xl',
  }[size];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Upvote Button */}
      <Button
        variant={userVote === 1 ? 'default' : 'ghost'}
        size="icon"
        className={cn(
          buttonSize,
          userVote === 1 && 'bg-green-100 hover:bg-green-200 text-green-700'
        )}
        onClick={handleUpvote}
        disabled={disabled}
        data-testid={`${testIdPrefix}-upvote`}
        title="Upvote"
      >
        <span className="select-none">{emojiUp}</span>
      </Button>

      {/* Score Display */}
      <div className="flex flex-col items-center min-w-[60px]">
        {/* Net votes or ELO score */}
        <div className={cn(
          'flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium',
          showTemperature && temp.bg,
          showTemperature && temp.color,
          !showTemperature && 'text-foreground'
        )}>
          {showTemperature && <span>{temp.emoji}</span>}
          <span>{netVotes >= 0 ? `+${netVotes}` : netVotes}</span>
        </div>
        
        {/* Total votes count */}
        {showVoteCount && (
          <span className="text-xs text-muted-foreground">
            {total} vote{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Downvote Button */}
      <Button
        variant={userVote === -1 ? 'destructive' : 'ghost'}
        size="icon"
        className={cn(
          buttonSize,
          userVote === -1 && 'bg-red-100 hover:bg-red-200 text-red-700'
        )}
        onClick={handleDownvote}
        disabled={disabled}
        data-testid={`${testIdPrefix}-downvote`}
        title="Downvote"
      >
        <span className="select-none">{emojiDown}</span>
      </Button>
    </div>
  );
};

/**
 * ELO Temperature Badge Component
 * Shows just the temperature indicator for a score
 */
export const EloTemperatureBadge = ({ score, showEmoji = true, className = '' }) => {
  const temp = getEloTemperature(score);
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
      temp.bg,
      temp.color,
      className
    )}>
      {showEmoji && <span>{temp.emoji}</span>}
      <span>{score.toLocaleString()}</span>
    </span>
  );
};

/**
 * ELO Score Display Component
 * Shows score with optional temperature indicator
 */
export const EloScoreDisplay = ({ 
  score, 
  showTemperature = true, 
  showLabel = false,
  size = 'md',
  className = '' 
}) => {
  const temp = getEloTemperature(score);
  
  const sizeClasses = {
    sm: 'text-sm px-2 py-0.5',
    md: 'text-base px-3 py-1',
    lg: 'text-lg px-4 py-1.5',
  }[size];
  
  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 rounded-full font-medium',
      showTemperature ? temp.bg : 'bg-gray-100',
      showTemperature ? temp.color : 'text-gray-700',
      sizeClasses,
      className
    )}>
      {showTemperature && <span>{temp.emoji}</span>}
      <span>{score.toLocaleString()}</span>
      {showLabel && <span className="text-xs opacity-75">ELO</span>}
    </div>
  );
};

export default EloVoteButtons;
